/* Patch 24 — useActionQueueSocket hook
 * --------------------------------------------------
 * Drop-in replacement for the polling pattern. Returns the same
 * action-queue summary shape the widgets used to fetch every 30s,
 * but now pushed live via WebSocket. Includes:
 *
 *   • Auto-reconnect with exponential back-off (1s → 30s cap)
 *   • Heartbeat-aware (server pings every 25s; missed-heartbeat
 *     watchdog forces a reconnect)
 *   • Duplicate suppression via event_id LRU
 *   • Snapshot-version gap detection on reconnect → fetches the
 *     authoritative summary from GET /api/action-queue/summary
 *   • HTTP fallback: after MAX_RECONNECTS failed attempts the hook
 *     degrades to 30s polling without surfacing an error.
 *
 * Usage:
 *   const { summary, status, lastEvent, refresh } = useActionQueueSocket();
 *   // status: 'connecting' | 'live' | 'reconnecting' | 'offline'
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { axiosInstance } from '../App';

const MAX_RECONNECTS    = 6;     // ~63s of attempts then degrade to polling
const HEARTBEAT_TIMEOUT = 40000; // 25s server ping + 15s grace
const POLL_INTERVAL_MS  = 30000; // offline / fallback polling cadence
const DEDUP_CAP         = 256;

function wsUrlFromBackend(token) {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  // https://x → wss://x ; http://x → ws://x
  const wsBase = base.replace(/^http/, 'ws');
  return `${wsBase}/api/ws/action-queue?token=${encodeURIComponent(token)}`;
}

export function useActionQueueSocket() {
  const [summary, setSummary]   = useState(null);
  const [status, setStatus]     = useState('connecting');
  const [lastEvent, setLastEvent] = useState(null);

  const wsRef         = useRef(null);
  const reconnectsRef = useRef(0);
  const lastSeqRef    = useRef(0);
  const lastVersionRef = useRef(0);
  const dedupRef      = useRef(new Set());
  const pollTimerRef  = useRef(null);
  const heartbeatRef  = useRef(null);
  const closedByCleanup = useRef(false);

  const rememberEventId = useCallback((id) => {
    if (!id) return false;
    if (dedupRef.current.has(id)) return true;
    dedupRef.current.add(id);
    if (dedupRef.current.size > DEDUP_CAP) {
      // Drop oldest (Set keeps insertion order).
      const first = dedupRef.current.values().next().value;
      dedupRef.current.delete(first);
    }
    return false;
  }, []);

  const fetchSnapshot = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/action-queue/summary');
      setSummary(r.data);
    } catch (e) {
      // Swallow — keep last good summary; widget will show stale data
      // with the offline badge.
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return;
    fetchSnapshot();
    pollTimerRef.current = setInterval(fetchSnapshot, POLL_INTERVAL_MS);
  }, [fetchSnapshot]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const armHeartbeatWatchdog = useCallback(() => {
    if (heartbeatRef.current) clearTimeout(heartbeatRef.current);
    heartbeatRef.current = setTimeout(() => {
      // Server ping missed — force reconnect.
      try { wsRef.current?.close(); } catch (_) { /* noop */ }
    }, HEARTBEAT_TIMEOUT);
  }, []);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setStatus('offline');
      return;
    }
    setStatus(reconnectsRef.current === 0 ? 'connecting' : 'reconnecting');
    const ws = new WebSocket(wsUrlFromBackend(token));
    wsRef.current = ws;

    ws.onopen = () => {
      // Successful handshake — but the server sends "hello" before
      // we treat ourselves as truly Live, so wait for that first.
      armHeartbeatWatchdog();
    };

    ws.onmessage = (raw) => {
      armHeartbeatWatchdog();
      let env;
      try { env = JSON.parse(raw.data); } catch (_) { return; }
      const { event_type, event_id, sequence, payload } = env || {};

      if (event_type === 'ping') return; // heartbeat only
      if (rememberEventId(event_id)) return; // duplicate

      if (typeof sequence === 'number') lastSeqRef.current = sequence;
      setLastEvent(env);

      if (event_type === 'hello' || event_type === 'action_queue.updated') {
        const v = payload?.snapshot_version;
        if (typeof v === 'number') lastVersionRef.current = v;
        if (payload?.summary) setSummary(payload.summary);
        if (event_type === 'hello') {
          reconnectsRef.current = 0;
          setStatus('live');
          stopPolling();
        }
        return;
      }

      if (event_type === 'resync_result') {
        // Server may report a snapshot_version gap; if so, refetch.
        const v = payload?.snapshot_version;
        if (typeof v === 'number' && v > lastVersionRef.current + 1) {
          fetchSnapshot();
          lastVersionRef.current = v;
        }
        return;
      }

      // posting.blocked / rfq.converted / overhead.allocated /
      // reconciliation.changed / quotation.approved / approval.step_decided
      // -> trigger a soft refresh of the summary (server will also
      //    debounce-emit an action_queue.updated within ~750ms, so we
      //    don't strictly need to refetch, but doing so guarantees the
      //    UI reflects truth even if the WS happens to drop right
      //    after the trigger event).
    };

    ws.onclose = () => {
      if (heartbeatRef.current) clearTimeout(heartbeatRef.current);
      if (closedByCleanup.current) return;
      if (reconnectsRef.current >= MAX_RECONNECTS) {
        setStatus('offline');
        startPolling();
        return;
      }
      reconnectsRef.current += 1;
      const delay = Math.min(30000, 1000 * Math.pow(2, reconnectsRef.current - 1));
      setStatus('reconnecting');
      setTimeout(() => {
        if (!closedByCleanup.current) connect();
      }, delay);
    };

    ws.onerror = () => {
      // Let onclose handle the back-off; just ensure we don't get
      // stuck in 'connecting'.
    };
  }, [armHeartbeatWatchdog, fetchSnapshot, rememberEventId, startPolling, stopPolling]);

  useEffect(() => {
    closedByCleanup.current = false;
    connect();
    return () => {
      closedByCleanup.current = true;
      stopPolling();
      if (heartbeatRef.current) clearTimeout(heartbeatRef.current);
      try { wsRef.current?.close(); } catch (_) { /* noop */ }
    };
  }, [connect, stopPolling]);

  const refresh = useCallback(() => fetchSnapshot(), [fetchSnapshot]);

  return { summary, status, lastEvent, refresh };
}

export default useActionQueueSocket;
